import { t } from '@lingui/macro';
import React from 'react';
import { Button } from '@3plearning/react-components';
import { ENDPOINT_PATH_PARTICIPATION } from '../../../helpers/constants';
import modalActions from '../../../containers/modal-container/modal-actions';
import Resources from '../../../helpers/resources';
import ImprovementTypePicker from '../../improvement-type-picker/improvement-type-picker';
import { SELECTION_AS_IS } from '../../../containers/participation/student/student-constants';
import ParticipationDataConverter from '../../../helpers/export-data-converter/participation-data-converter';
import { ALL_GROUPS } from '../../report-filters/filter-constants';
import {
    EXPORT_RESOURCE_METHODS,
    REPORT_LEVEL_ALL_YEARS,
    REPORT_LEVEL_ALL_CLASSES,
    REPORT_LEVEL_SINGLE_CLASS,
    REPORT_LEVEL_GROUP,
    REPORT_LEVEL_STUDENT,
    REPORT_LEVEL_YEAR_GROUP_TEACHER,
    FILE_FORMAT
} from './export-report-actions';
import activeFiltersSelectors from '../../report-filters/redux/active-filters/active-filters-selectors';
import {
    REPORT_STATE_ALL_CLASSES,
    REPORT_STATE_ALL_YEARS,
    REPORT_STATE_TOP_TEN
} from '../../../containers/participation/all-classes/report-state/report-state-actions';
import { localeCode } from '../../../helpers/locale-helpers';

/**
 * Calculates the export parameters needed for the current participation report
 * Handles displaying a modal to the user asking them which improvement type they would
 * like to use when exporting student report
 */
export default async function exportParticipationReport(
    getState,
    reportLevel,
    dispatch,
    i18n,
    fileFormat
) {
    const state = getState();
    const { participationAllClasses, participationSingleClass } = state;
    const selectedDate = activeFiltersSelectors.selectDate(state);
    const resourcesMethod = EXPORT_RESOURCE_METHODS[reportLevel];
    const path = {
        reportType: ENDPOINT_PATH_PARTICIPATION,
        fileFormat: fileFormat === 0 ? FILE_FORMAT.PDF : FILE_FORMAT.EXCEL
    };
    const sessionId = state.sessionId;

    let data;
    let title = '';

    switch (reportLevel) {
        case REPORT_LEVEL_ALL_YEARS:
        case REPORT_LEVEL_ALL_CLASSES:
        case REPORT_LEVEL_YEAR_GROUP_TEACHER: {
            const {
                tableTitle,
                reportTitle
            } = getParticipationAllClassesTableTitle({
                i18n,
                reportLevel,
                reportState: state.reportState
            });
            const participationKey = getParticipationAllClassesParticipationKey(
                {
                    reportLevel
                }
            );
            title = reportTitle;

            data = ParticipationDataConverter.convertAllClasses(
                participationAllClasses,
                selectedDate,
                activeFiltersSelectors.selectExportReportHeading(state),
                participationKey,
                tableTitle,
                sessionId
            );

            if (reportLevel === REPORT_LEVEL_YEAR_GROUP_TEACHER) {
                path.yearGroupId = activeFiltersSelectors.selectYearGroupIdForApi(
                    state
                );
                path.teacherId = activeFiltersSelectors.selectTeacherIdForApi(
                    state
                );
            }
            await downloadParticipationReport({
                resourcesMethod,
                data,
                path,
                title
            });

            break;
        }

        case REPORT_LEVEL_SINGLE_CLASS:
        case REPORT_LEVEL_GROUP: {
            const tableTitle = i18n._(t`Export - All groups`);
            const selectedGroupId = activeFiltersSelectors.selectGroupId(state);
            const groupId =
                reportLevel === REPORT_LEVEL_GROUP
                    ? activeFiltersSelectors.selectGroupId(state)
                    : null;
            data = ParticipationDataConverter.convertSingleClass(
                participationSingleClass,
                selectedDate,
                groupId,
                tableTitle
            );
            title = getParticipationGroupsTableTitle({
                classroomName: data.classroomParticipationData.classroomName,
                dateRange: data.dateRange,
                classroomGroupParticipations:
                    data.classroomParticipationData
                        .classroomGroupParticipations,
                i18n,
                selectedGroupId
            });
            path.classroomId = activeFiltersSelectors.selectClassId(state);
            await downloadParticipationReport({
                resourcesMethod,
                data,
                path,
                title,
                params: {
                    isForAllGroups: selectedGroupId === ALL_GROUPS
                }
            });

            break;
        }

        case REPORT_LEVEL_STUDENT:
            await openImprovementTypeModal(getState, dispatch, i18n);
            break;
        default:
            break;
    }
}

/**
 * Opens modal for user to choose their improvement calculation for the exported student
 * participation report
 */
async function openImprovementTypeModal(getState, dispatch, i18n) {
    let improvementType = SELECTION_AS_IS;
    dispatch(
        modalActions.openModal({
            title: 'Select improvement calculation',
            content: (
                <ImprovementTypePicker
                    initialSelection={improvementType}
                    pickImprovementType={(type) => {
                        improvementType = type;
                    }}
                />
            ),
            buttons: [
                { text: 'Cancel', danger: true },
                {
                    text: 'Export',
                    primary: true,
                    type: Button.LOZENGE,
                    handler: () => {
                        exportStudentParticipation(
                            improvementType,
                            getState,
                            dispatch,
                            i18n
                        );
                    }
                }
            ]
        })
    );
}

/**
 * Exports student participation report following user selecting improvement type
 */
async function exportStudentParticipation(
    selectedImprovement,
    getState,
    dispatch,
    i18n
) {
    const state = getState();
    const { participationStudent, selectedImprovements } = state;

    const data = ParticipationDataConverter.convertStudent(
        participationStudent,
        activeFiltersSelectors.selectDate(state),
        selectedImprovement,
        selectedImprovements
    );
    const reportTitleSuffix = i18n._('Export - Student');

    await downloadParticipationReport({
        resourcesMethod: 'studentExport',
        path: {
            reportType: ENDPOINT_PATH_PARTICIPATION,
            classroomId: activeFiltersSelectors.selectClassId(state),
            userProfileId: activeFiltersSelectors.selectStudentId(state)
        },
        data,
        title: `${data.studentParticipationData.firstName}${data.studentParticipationData.lastName}_${reportTitleSuffix}`
    });

    dispatch(modalActions.closeModal());
}

function getParticipationAllClassesParticipationKey({ reportLevel }) {
    let participationKey = 'classroomsParticipationData';
    if (reportLevel === REPORT_LEVEL_ALL_YEARS)
        participationKey = 'yearGroupParticipationData';

    return participationKey;
}

function getParticipationGroupsTableTitle({
    classroomName,
    dateRange,
    classroomGroupParticipations,
    i18n,
    selectedGroupId
}) {
    let groupName = i18n._('Export - All groups');

    if (selectedGroupId !== ALL_GROUPS) {
        classroomGroupParticipations.forEach((group) => {
            if (group.id === selectedGroupId) groupName = group.name;
        });
    }

    const localisedFileName = i18n._(
        t`Filename - Export - Groups - ${classroomName} - ${groupName} - ${dateRange}`
    );
    return localisedFileName.replace(/\./g, '-');
}

function getParticipationAllClassesTableTitle({
    i18n,
    reportLevel,
    reportState
}) {
    const TEXT_KEYS = {
        DEFAULT: 'Export - Mathletics Classes',
        DEFAULT_REPORT: 'Export - All classes - ParticipationReport',
        TOP_STUDENTS: 'Export - Top Students',
        TOP_STUDENTS_REPORT: 'Export - Top Students - ParticipationReport',
        ALL_YEARS: 'Export - All years',
        ALL_YEARS_REPORT: 'Export - All years - ParticipationReport',
        ALL_CLASSES: 'Export - All classes',
        ALL_CLASSES_REPORT: 'Export - All classes - ParticipationReport'
    };

    let key = 'DEFAULT';

    if (reportLevel === REPORT_LEVEL_ALL_YEARS) {
        if (reportState === REPORT_STATE_TOP_TEN) {
            key = 'TOP_STUDENTS';
        } else if (reportState !== REPORT_STATE_ALL_CLASSES) {
            key = 'ALL_YEARS';
        } else {
            key = 'ALL_CLASSES';
        }
    } else if (reportLevel === REPORT_LEVEL_YEAR_GROUP_TEACHER) {
        if (reportState === REPORT_STATE_ALL_YEARS) {
            key = 'ALL_CLASSES';
        }
    }

    return {
        tableTitle: i18n._(TEXT_KEYS[key]),
        reportTitle: i18n._(TEXT_KEYS[`${key}_REPORT`]),
        reportLevelHeader: 'Test'
    };
}

async function generateDownloadableFile({ response, title }) {
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = window.document.createElement('a');
    link.href = url;
    link.setAttribute('download', title);
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

async function downloadParticipationReport({
    resourcesMethod,
    path,
    data,
    title,
    params = {}
}) {
    const locale = localeCode();
    try {
        const { valid, data: downloadKey } = await Resources[resourcesMethod]
            .post(path)
            .withBody(data)
            .withParams({
                ...params,
                locale
            })
            .send();

        if (!valid) throw new Error('Invalid download key response');

        const appendResourceMethod = `${resourcesMethod}Key`;
        const urlResponse = await Resources[appendResourceMethod]
            .get({ ...path, downloadKey })
            .raw()
            .send();
        const response = await fetch(urlResponse.verb.url);

        if (!response.ok) throw new Error('Network error');

        await generateDownloadableFile({ response, title });
    } catch (error) {
        console.error('Error downloading participation report:', error);
    }
}



// WEBPACK FOOTER //
// ./components/sub-heading/export-report/export-participation-report.js